import { ZERO, abs, cents, sub, sum, type Cents } from '../money.js';
import type { ItemId, Receipt } from '../types.js';

/**
 * Deterministic arithmetic checks over a parsed receipt.
 *
 * Everything here is integer-cent arithmetic in plain TypeScript. The model is
 * never asked whether its own numbers add up — that is exactly the question a
 * 2B model is worst at, and the one where a wrong answer costs real money.
 *
 * Issues are returned as a list rather than a boolean because the repair UI
 * needs to highlight *which* field is wrong, and the escalation policy needs to
 * know how badly the parse failed.
 */

export type IssueCode =
  | 'no-items'
  | 'nonpositive-qty'
  | 'item-line-total-mismatch'
  | 'items-subtotal-mismatch'
  | 'unexplained-residual'
  | 'missing-total'
  | 'implausible-price';

export interface ValidationIssue {
  code: IssueCode;
  severity: 'error' | 'warning';
  message: string;
  itemId?: ItemId;
  /** Field on the item or receipt that the repair UI should focus. */
  field?: string;
  expected?: Cents;
  actual?: Cents;
}

export interface ValidationOptions {
  /** Per-line slack, absorbing unit-price rounding (3 × 3.33 vs 10.00). */
  lineTolerance?: number;
  /** Slack on aggregate sums, where per-line roundings accumulate. */
  aggregateTolerance?: number;
  /** Above this, a single unit price is presumed an OCR misread. */
  implausibleUnitPrice?: number;
}

const DEFAULTS = {
  lineTolerance: 1,
  aggregateTolerance: 2,
  implausibleUnitPrice: 10_000_00,
} satisfies Required<ValidationOptions>;

/** Sum of item line totals — the one figure we always trust, because we own it. */
export function itemsTotal(receipt: Receipt): Cents {
  return sum(receipt.items.map((item) => item.lineTotal));
}

export function validateReceipt(
  receipt: Receipt,
  options: ValidationOptions = {},
): ValidationIssue[] {
  const { lineTolerance, aggregateTolerance, implausibleUnitPrice } = {
    ...DEFAULTS,
    ...options,
  };
  const issues: ValidationIssue[] = [];

  if (receipt.items.length === 0) {
    issues.push({
      code: 'no-items',
      severity: 'error',
      message: 'No line items were read from the receipt.',
    });
  }

  for (const item of receipt.items) {
    if (!Number.isInteger(item.qty) || item.qty < 1) {
      issues.push({
        code: 'nonpositive-qty',
        severity: 'error',
        message: `"${item.name}" has quantity ${item.qty}.`,
        itemId: item.id,
        field: 'qty',
      });
      continue;
    }

    const expected = cents(item.unitPrice * item.qty);
    if (Math.abs(expected - item.lineTotal) > lineTolerance) {
      issues.push({
        code: 'item-line-total-mismatch',
        severity: 'error',
        message: `"${item.name}": ${item.qty} × unit price does not match the line total.`,
        itemId: item.id,
        field: 'lineTotal',
        expected,
        actual: item.lineTotal,
      });
    }

    if (Math.abs(item.unitPrice) > implausibleUnitPrice) {
      issues.push({
        code: 'implausible-price',
        severity: 'warning',
        message: `"${item.name}" has an unusually large unit price — likely a misread decimal point.`,
        itemId: item.id,
        field: 'unitPrice',
        actual: item.unitPrice,
      });
    }
  }

  const lines = itemsTotal(receipt);

  if (receipt.subtotal !== undefined) {
    if (Math.abs(receipt.subtotal - lines) > aggregateTolerance) {
      issues.push({
        code: 'items-subtotal-mismatch',
        severity: 'error',
        message: 'Line items do not add up to the printed subtotal.',
        field: 'subtotal',
        expected: lines,
        actual: receipt.subtotal,
      });
    }
  }

  if (receipt.total === undefined) {
    issues.push({
      code: 'missing-total',
      severity: 'error',
      message: 'No total was found on the receipt.',
      field: 'total',
    });
  } else {
    // Whatever the printed total exceeds items + tax + tip by is real money
    // someone has to pay — a service charge, a cover, a rounding line we did
    // not model. Surface it rather than silently absorbing it.
    const accounted = sum([
      receipt.subtotal ?? lines,
      receipt.tax ?? ZERO,
      receipt.tip ?? ZERO,
    ]);
    const residual = sub(receipt.total, accounted);
    if (abs(residual) > aggregateTolerance) {
      issues.push({
        code: 'unexplained-residual',
        severity: 'warning',
        message:
          residual > 0
            ? `The total is ${(residual / 100).toFixed(2)} more than items plus tax and tip — usually a tip or service charge the reader missed. It will be shared out as extras unless you file it.`
            : `The total is ${(-residual / 100).toFixed(2)} less than items plus tax and tip — often a discount the reader missed.`,
        field: 'total',
        expected: accounted,
        actual: receipt.total,
      });
    }
  }

  return issues;
}

export const errorsOnly = (issues: ValidationIssue[]): ValidationIssue[] =>
  issues.filter((issue) => issue.severity === 'error');

/**
 * True when the parse is unsound enough to be worth a retry or a cloud attempt.
 * Warnings deliberately do not trigger this — a service charge is not a failure.
 */
export const shouldEscalate = (issues: ValidationIssue[]): boolean =>
  errorsOnly(issues).length > 0;

/**
 * Fill in totals that follow unambiguously from what was read.
 *
 * Only subtotal and total are derived. Tax is deliberately never inferred from
 * a residual — that would invent a tax rate, and a plausible-looking invented
 * number is worse than an absent one.
 */
export function deriveMissingTotals(receipt: Receipt): Receipt {
  const lines = itemsTotal(receipt);
  const subtotal = receipt.subtotal ?? lines;
  const total =
    receipt.total ?? sum([subtotal, receipt.tax ?? ZERO, receipt.tip ?? ZERO]);
  return { ...receipt, subtotal, total };
}
